import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    InputAdornment,
    Tooltip,
    Checkbox,
    FormControl,
    Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PercentIcon from '@mui/icons-material/Percent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useFormContext } from 'react-hook-form';
import { useResponsive } from '../../../../../utils/useResponsive';
import { request } from '../../../../../utils/request';
import { ItemsTableSectionProps, Product, ProductItem } from '../../../types';
import { useNavigate } from 'react-router-dom';

// Default tax options (GST slabs for India)
const defaultTaxOptions = [
    { value: 'none', label: 'None', rate: 0 },
    { value: 5, label: 'GST 5%', rate: 5 },
    { value: 12, label: 'GST 12%', rate: 12 },
    { value: 18, label: 'GST 18%', rate: 18 },
    { value: 28, label: 'GST 28%', rate: 28 },
    { value: 40, label: 'GST 40%', rate: 40 },
    { value: 'custom', label: 'Custom' },
];

const ItemsTableSection: React.FC<ItemsTableSectionProps> = ({
    // Form field names
    formFieldName = 'items',

    // Labels with defaults
    addItemButtonText = 'Add Item',
    srNoLabel = 'Sr No',
    itemLabel = 'Item',
    hsnNoLabel = 'HSN No',
    qtyLabel = 'Qty',
    priceLabel = 'Price',
    discountLabel = 'Discount',
    taxLabel = 'Tax',
    amountLabel = 'Amount',
    actionsLabel = 'Actions',
    subtotalLabel = 'Subtotal',
    totalDiscountLabel = 'Total Discount',
    totalTaxLabel = 'Total Tax',
    taxableAmountLabel = 'Taxable Amount',
    onTaxBreakdownChange,

    // Values
    items: propItems = [],

    // Callbacks
    onItemsChange,
    onSubtotalChange,
    onTotalDiscountChange,
    onTotalTaxChange,
    onTaxableAmountChange,
    onTotalAmountChange,
    onItemTaxesChange,

    // Configuration with defaults
    showSrNo = true,
    showHsnNo = true,
    showDiscount = true,
    showTax = true,
    showActions = true,
    disabled = false,
    compact = false,
    maxItems = 50,
    showTaxableAmount = true,
    
    // NEW: Purchase mode flag
    isPurchase = false, // Default to false (sales mode)

    // Additional columns
    extraColumns = [],

    // API endpoint
    productsApiEndpoint = 'product/get-all-products',
}) => {
    // State
    const [items, setItems] = useState<ProductItem[]>(propItems);
    const [openProductModal, setOpenProductModal] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [isSelectAll, setIsSelectAll] = useState(false);

    const navigate = useNavigate();

    // Responsive hook
    const { isMobile, isTablet, isMobileOrTablet } = useResponsive();
    const shouldBeCompact = compact || isMobileOrTablet;

    // React Hook Form integration
    const { setValue, watch } = useFormContext?.() || {};

    // Get form values if using react-hook-form
    const formItems = formFieldName ? watch?.(formFieldName) : undefined;

    // Use form values if available, otherwise use props
    const displayItems = formItems !== undefined ? formItems : items;

    // Fetch products from API
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await request({
                    url: productsApiEndpoint,
                    method: 'get',
                });

                if (res.status === 'success' && res.products && Array.isArray(res.products)) {
                    // Map API response to Product interface
                    const mappedProducts = res.products.map((product: any) => ({
                        _id: product._id,
                        name: product.name || '',
                        partNo: product.partNo || '',
                        quantity: product.quantity || 0,
                        sellingPriceB2C: product.sellingPriceB2C || 0,
                        sellingPriceB2B: product.sellingPriceB2B || 0,
                        mrp: product.mrp || 0,
                        unitPrice: product.unitPrice || 0,
                        category: product.category || '',
                        barcode: product.barcode || ''
                    }));
                    setProducts(mappedProducts);
                }
            } catch (error) {
                console.error('Error fetching products:', error);
            }
        };
        fetchProducts();
    }, [productsApiEndpoint]);

    // Calculate item amount
    const calculateItemAmount = (item: ProductItem): number => {
        const quantity = item.quantity || 1;
        const price = item.price || 0;
        let discountAmount = 0;

        // Calculate discount
        if (item.discount && item.discount.amount > 0) {
            if (item.discount.isPercentage) {
                discountAmount = (price * quantity * item.discount.amount) / 100;
            } else {
                discountAmount = item.discount.amount;
            }
        }

        // Calculate base amount
        const baseAmount = (price * quantity) - discountAmount;

        // Calculate tax
        let taxAmount = 0;
        if (item.tax && item.tax.type !== 'none') {
            const taxRate = item.tax.rate || 0;
            taxAmount = (baseAmount * taxRate) / 100;
        }

        // Total amount
        return baseAmount + taxAmount;
    };

    // Update item in the list
    const updateItem = (id: string, field: keyof ProductItem | 'discount.amount' | 'discount.isPercentage' | 'tax.type' | 'tax.rate', value: any) => {
        const updatedItems = displayItems.map((item: ProductItem) => {
            if (item.id === id) {
                const updatedItem = { ...item };

                if (field.includes('discount.')) {
                    const discountField = field.split('.')[1] as 'amount' | 'isPercentage';
                    updatedItem.discount = {
                        ...updatedItem.discount,
                        [discountField]: value
                    };
                } else if (field.includes('tax.')) {
                    const taxField = field.split('.')[1] as 'type' | 'rate';
                    updatedItem.tax = {
                        ...updatedItem.tax,
                        [taxField]: value
                    };

                    // If tax type changes to 'none', set rate to 0
                    if (taxField === 'type' && value === 'none') {
                        updatedItem.tax.rate = 0;
                    }
                } else {
                    (updatedItem as any)[field] = value;
                }

                // Recalculate amount
                updatedItem.amount = calculateItemAmount(updatedItem);

                return updatedItem;
            }
            return item;
        });

        updateItems(updatedItems);
    };

    // Remove item from list
    const removeItem = (id: string) => {
        const updatedItems = displayItems.filter((item: ProductItem) => item.id !== id);

        // Update serial numbers
        const renumberedItems = updatedItems.map((item: any, index: any) => ({
            ...item,
            srNo: index + 1
        }));

        updateItems(renumberedItems);
    };

    // Update items
    const updateItems = (updatedItems: ProductItem[]) => {
        setItems(updatedItems);
        if (formFieldName && setValue) {
            setValue(formFieldName, updatedItems);
        }
        if (onItemsChange) {
            onItemsChange(updatedItems);
        }
    };

    // Add selected products from modal
    const addSelectedProducts = () => {
        const selectedProductsData = products.filter(product =>
            selectedProducts.includes(product._id)
        );

        const newItems = selectedProductsData.map((product, index) => {
            const existingCount = displayItems.length;
            
            // For purchase mode, set price to 0 (user will enter manually)
            // For sales mode, use the selling price
            const initialPrice = isPurchase ? 0 : (product.unitPrice || 0);
            
            return {
                id: `${product._id}_${Date.now()}_${index}`,
                srNo: existingCount + index + 1,
                itemName: product.name,
                hsnNo: product.partNo || '', // Using partNo as HSN code
                quantity: 1,
                price: initialPrice,
                discount: {
                    amount: 0,
                    isPercentage: false
                },
                tax: {
                    type: 'none',
                    rate: 0
                },
                amount: initialPrice,
                productId: product._id
            };
        });

        const updatedItems = [...displayItems, ...newItems];
        updateItems(updatedItems);
        setSelectedProducts([]);
        setIsSelectAll(false);
        setOpenProductModal(false);
    };

    // Handle product selection in modal
    const handleProductSelect = (productId: string) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    // Handle select all in modal
    const handleSelectAll = () => {
        if (isSelectAll) {
            setSelectedProducts([]);
            setIsSelectAll(false);
        } else {
            const allProductIds = filteredProducts.map(product => product._id);
            setSelectedProducts(allProductIds);
            setIsSelectAll(true);
        }
    };

    const totals = useMemo(() => {
        let subtotal = 0;
        let totalDiscount = 0;
        let totalTax = 0;
        let taxableAmount = 0;
        const itemTaxes: { itemId: string; taxAmount: number }[] = [];
        const taxRatesMap = new Map<number, { sgst: number; cgst: number; igst: number }>();

        displayItems.forEach((item: ProductItem) => {
            const quantity = item.quantity || 1;
            const price = item.price || 0;
            const baseAmount = price * quantity;

            let itemDiscount = 0;
            if (item.discount?.amount > 0) {
                itemDiscount = item.discount.isPercentage
                    ? (baseAmount * item.discount.amount) / 100
                    : item.discount.amount;
            }

            const itemTaxableAmount = baseAmount - itemDiscount;

            let itemTax = 0;
            if (item.tax?.type !== 'none' && item.tax?.rate > 0) {
                itemTax = (itemTaxableAmount * item.tax.rate) / 100;
                itemTaxes.push({ itemId: item.id, taxAmount: itemTax });

                if (!taxRatesMap.has(item.tax.rate)) {
                    taxRatesMap.set(item.tax.rate, {
                        sgst: item.tax.type === 'gst' ? item.tax.rate / 2 : 0,
                        cgst: item.tax.type === 'gst' ? item.tax.rate / 2 : 0,
                        igst: item.tax.type === 'custom' ? item.tax.rate : 0,
                    });
                }
            }

            subtotal += baseAmount;
            totalDiscount += itemDiscount;
            totalTax += itemTax;
            taxableAmount += itemTaxableAmount;
        });

        return {
            subtotal,
            totalDiscount,
            totalTax,
            taxableAmount,
            totalAmount: taxableAmount + totalTax,
            itemTaxes,
            taxBreakdown: Array.from(taxRatesMap.values()),
        };
    }, [displayItems]);

    useEffect(() => {
        onSubtotalChange?.(totals.subtotal);
        onTotalDiscountChange?.(totals.totalDiscount);
        onTotalTaxChange?.(totals.totalTax);
        onTaxableAmountChange?.(totals.taxableAmount);
        onTotalAmountChange?.(totals.totalAmount);
        onItemTaxesChange?.(totals.itemTaxes);
        onTaxBreakdownChange?.(totals.taxBreakdown);
    }, [onItemTaxesChange, onSubtotalChange, onTaxBreakdownChange, onTaxableAmountChange, onTotalAmountChange, onTotalDiscountChange, onTotalTaxChange, totals]);

    const { totalDiscount, totalTax, taxableAmount, totalAmount } = totals;

    // Format currency
    const formatCurrency = (amount: number): string => {
        if (isNaN(amount)) return '₹0.00';

        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Filter products based on search
    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.partNo && product.partNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Get tax display value
    const getTaxDisplayValue = (item: ProductItem) => {
        if (item.tax.type === 'custom') return 'custom';
        if (item.tax.type === 'none') return 'none';
        return item.tax.rate || 'none';
    };

    // Handle tax change
    const handleTaxChange = (id: string, value: any) => {
        const updatedItems = displayItems.map((item: ProductItem) => {
            if (item.id !== id) return item;

            const updatedItem = { ...item };

            if (value === 'none') {
                updatedItem.tax = { type: 'none', rate: 0 };
            } else if (value === 'custom') {
                updatedItem.tax = { type: 'custom', rate: 0 };
            } else {
                updatedItem.tax = { type: 'gst', rate: Number(value) };
            }

            updatedItem.amount = calculateItemAmount(updatedItem);
            return updatedItem;
        });

        updateItems(updatedItems);
    };

    // Responsive table cell padding
    const getTableCellPadding = () => {
        if (isMobile) return '4px 4px';
        if (isTablet) return '4px 4px';
        return '4px 18px';
    };

    // Responsive font sizes
    const getFontSize = () => {
        if (isMobile) return '0.75rem';
        if (isTablet) return '0.85rem';
        return '0.875rem';
    };

    // Responsive totals layout
    const totalsLayout = isMobile ? 'column' : 'row';

    return (
        <Box sx={{ height: "100%" }}>
            {/* Items Table */}
            <TableContainer
                component={Paper}
                sx={{
                    mb: 0,
                    overflowX: 'auto'
                }}
            >
                <Table size={shouldBeCompact ? "small" : "medium"}>
                    <TableHead sx={{ backgroundColor: "#c7c7c7" }}>
                        <TableRow>
                            {showSrNo && (
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: getFontSize(),
                                    p: getTableCellPadding(),
                                    minWidth: isMobile ? '60px' : '80px'
                                }}>
                                    {srNoLabel}
                                </TableCell>
                            )}
                            <TableCell sx={{
                                fontWeight: 'bold',
                                fontSize: getFontSize(),
                                p: getTableCellPadding(),
                                minWidth: isMobile ? '120px' : '150px'
                            }}>{itemLabel}</TableCell>
                            {showHsnNo && (
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: getFontSize(),
                                    p: getTableCellPadding(),
                                    minWidth: isMobile ? '80px' : '170px'
                                }}>{hsnNoLabel}</TableCell>
                            )}
                            <TableCell sx={{
                                fontWeight: 'bold',
                                fontSize: getFontSize(),
                                p: getTableCellPadding(),
                                minWidth: isMobile ? '60px' : '100px'
                            }}>{qtyLabel}</TableCell>
                            <TableCell sx={{
                                fontWeight: 'bold',
                                fontSize: getFontSize(),
                                p: getTableCellPadding(),
                                minWidth: isMobile ? '80px' : '200px'
                            }}>{priceLabel}</TableCell>
                            {showDiscount && (
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: getFontSize(),
                                    p: getTableCellPadding(),
                                    minWidth: isMobile ? '100px' : '200px'
                                }}>{discountLabel}</TableCell>
                            )}
                            {showTax && (
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: getFontSize(),
                                    p: getTableCellPadding(),
                                    minWidth: isMobile ? '100px' : '150px'
                                }}>{taxLabel}</TableCell>
                            )}

                            {/* Extra Columns */}
                            {extraColumns.map(column => (
                                <TableCell key={column.id} sx={{
                                    fontWeight: 'bold',
                                    fontSize: getFontSize(),
                                    p: getTableCellPadding()
                                }}>
                                    {column.label}
                                </TableCell>
                            ))}

                            <TableCell sx={{
                                fontWeight: 'bold',
                                fontSize: getFontSize(),
                                p: getTableCellPadding(),
                                minWidth: isMobile ? '90px' : '100px'
                            }}>{amountLabel}</TableCell>

                            {showActions && (
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: getFontSize(),
                                    p: getTableCellPadding(),
                                    minWidth: isMobile ? '60px' : '70px'
                                }}>{actionsLabel}</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayItems.length === 0 ? (
                            <>
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            3 +
                                            (showSrNo ? 1 : 0) +
                                            (showHsnNo ? 1 : 0) +
                                            (showDiscount ? 1 : 0) +
                                            (showTax ? 1 : 0) +
                                            extraColumns.length +
                                            (showActions ? 1 : 0)
                                        }
                                        align="center"
                                        sx={{ py: 1 }}
                                    >
                                        <Typography color="text.secondary">
                                            No items added. Click "Add Item" to add products.
                                        </Typography>
                                    </TableCell>

                                </TableRow>

                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            3 +
                                            (showSrNo ? 1 : 0) +
                                            (showHsnNo ? 1 : 0) +
                                            (showDiscount ? 1 : 0) +
                                            (showTax ? 1 : 0) +
                                            extraColumns.length +
                                            (showActions ? 1 : 0)
                                        }
                                        sx={{
                                            p: isMobile ? '6px 4px' : '6px 5px',
                                            textAlign: 'center',
                                            py: 1
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => setOpenProductModal(true)}
                                            disabled={disabled || displayItems.length >= maxItems}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{
                                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                py: isMobile ? 0.5 : .4,
                                                px: isMobile ? 1.5 : 2,
                                                width: '100%',
                                                border: '1px dashed #7047ef'
                                            }}
                                        >
                                            {addItemButtonText}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </>

                        ) : (
                            <>
                                {displayItems.map((item: ProductItem, index: number) => (
                                    <TableRow key={item.id}>
                                        {showSrNo && (
                                            <TableCell sx={{
                                                fontSize: getFontSize(),
                                                p: getTableCellPadding()
                                            }}>
                                                <Typography>{item.srNo}</Typography>
                                            </TableCell>
                                        )}

                                        <TableCell sx={{ p: getTableCellPadding() }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={item.itemName}
                                                onChange={(e) => updateItem(item.id, 'itemName', e.target.value)}
                                                disabled={disabled}
                                                placeholder="Item name"
                                                sx={{
                                                    fontSize: getFontSize(),
                                                    '& .MuiInputBase-input': {
                                                        fontSize: getFontSize(),
                                                        p: isMobile ? '6px 8px' : '8px 12px'
                                                    }
                                                }}
                                            />
                                        </TableCell>

                                        {showHsnNo && (
                                            <TableCell sx={{ p: getTableCellPadding() }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={item.hsnNo}
                                                    onChange={(e) => updateItem(item.id, 'hsnNo', e.target.value)}
                                                    disabled={disabled}
                                                    placeholder="HSN Code"
                                                    sx={{
                                                        fontSize: getFontSize(),
                                                        '& .MuiInputBase-input': {
                                                            fontSize: getFontSize(),
                                                            p: isMobile ? '6px 8px' : '8px 12px'
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                        )}

                                        <TableCell sx={{ p: getTableCellPadding() }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const parsedValue = parseFloat(value);
                                                    updateItem(item.id, 'quantity', isNaN(parsedValue) ? 1 : parsedValue);
                                                }}
                                                disabled={disabled}
                                                InputProps={{
                                                    inputProps: {
                                                        min: 1,
                                                        step: 1,
                                                        style: {
                                                            fontSize: getFontSize(),
                                                            padding: isMobile ? '6px 8px' : '8px 12px'
                                                        }
                                                    }
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell sx={{ p: getTableCellPadding() }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={item.price}
                                                onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                                                disabled={disabled}
                                                placeholder={isPurchase ? "Enter purchase price" : "Auto-filled"}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start" sx={{ fontSize: getFontSize() }}>
                                                            ₹
                                                        </InputAdornment>
                                                    ),
                                                    inputProps: {
                                                        min: 0,
                                                        step: 0.01,
                                                        style: {
                                                            fontSize: getFontSize(),
                                                            padding: isMobile ? '6px 8px' : '8px 12px'
                                                        }
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        backgroundColor: isPurchase ? 'white' : '#f5f5f5',
                                                    }
                                                }}
                                            />
                                        </TableCell>

                                        {showDiscount && (
                                            <TableCell sx={{ p: getTableCellPadding() }}>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        value={item.discount?.amount || 0}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const parsedValue = parseFloat(value);
                                                            updateItem(item.id, 'discount.amount', isNaN(parsedValue) ? 0 : parsedValue);
                                                        }}
                                                        disabled={disabled}
                                                        InputProps={{
                                                            startAdornment: item.discount?.isPercentage ? (
                                                                <InputAdornment position="start" sx={{ fontSize: getFontSize() }}>
                                                                    <PercentIcon fontSize="small" />
                                                                </InputAdornment>
                                                            ) : (
                                                                <InputAdornment position="start" sx={{ fontSize: getFontSize() }}>
                                                                    ₹
                                                                </InputAdornment>
                                                            ),
                                                            inputProps: {
                                                                min: 0,
                                                                style: {
                                                                    fontSize: getFontSize(),
                                                                    padding: isMobile ? '6px 8px' : '8px 12px'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <Tooltip title={item.discount?.isPercentage ? "Switch to Amount" : "Switch to Percentage"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => updateItem(item.id, 'discount.isPercentage', !item.discount?.isPercentage)}
                                                            disabled={disabled}
                                                            sx={{ fontSize: getFontSize() }}
                                                        >
                                                            {item.discount?.isPercentage ? (
                                                                <PercentIcon fontSize="small" />
                                                            ) : (
                                                                <AttachMoneyIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        )}

                                        {showTax && (
                                            <TableCell sx={{ p: getTableCellPadding() }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={getTaxDisplayValue(item)}
                                                        onChange={(e) => handleTaxChange(item.id, e.target.value)}
                                                        disabled={disabled}
                                                        sx={{
                                                            fontSize: getFontSize(),
                                                            '& .MuiSelect-select': {
                                                                fontSize: getFontSize(),
                                                                p: isMobile ? '6px 8px' : '8px 12px'
                                                            }
                                                        }}
                                                    >
                                                        {defaultTaxOptions.map((option) => (
                                                            <MenuItem
                                                                key={typeof option.value === 'number' ? option.value : option.value}
                                                                value={option.value}
                                                                sx={{ fontSize: getFontSize() }}
                                                            >
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                {(item.tax.type === 'custom' || (item.tax.type === 'gst' && item.tax.rate === 0)) && (
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        value={item.tax.rate || 0}
                                                        onChange={(e) => updateItem(item.id, 'tax.rate', parseFloat(e.target.value) || 0)}
                                                        disabled={disabled}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end" sx={{ fontSize: getFontSize() }}>
                                                                    %
                                                                </InputAdornment>
                                                            ),
                                                            inputProps: {
                                                                min: 0,
                                                                max: 100,
                                                                step: 0.01,
                                                                style: {
                                                                    fontSize: getFontSize(),
                                                                    padding: isMobile ? '6px 8px' : '8px 12px'
                                                                }
                                                            }
                                                        }}
                                                        sx={{ mt: 0.5 }}
                                                        placeholder="Tax rate"
                                                    />
                                                )}
                                            </TableCell>
                                        )}

                                        {/* Extra Columns */}
                                        {extraColumns.map(column => (
                                            <TableCell key={column.id} sx={{ p: getTableCellPadding() }}>
                                                {column.render(item, index)}
                                            </TableCell>
                                        ))}

                                        <TableCell sx={{ p: getTableCellPadding() }}>
                                            <Typography fontWeight="medium" fontSize={getFontSize()}>
                                                {formatCurrency(item.amount)}
                                            </Typography>
                                        </TableCell>

                                        {showActions && (
                                            <TableCell sx={{ p: getTableCellPadding() }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeItem(item.id)}
                                                    disabled={disabled}
                                                    color="error"
                                                    sx={{ fontSize: getFontSize() }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}

                                {/* Add Item button row */}
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            3 +
                                            (showSrNo ? 1 : 0) +
                                            (showHsnNo ? 1 : 0) +
                                            (showDiscount ? 1 : 0) +
                                            (showTax ? 1 : 0) +
                                            extraColumns.length +
                                            (showActions ? 1 : 0)
                                        }
                                        sx={{
                                            p: isMobile ? '6px 4px' : '6px 4px',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => setOpenProductModal(true)}
                                            disabled={disabled || displayItems.length >= maxItems}
                                            size={isMobile ? "small" : "small"}
                                            sx={{
                                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                py: isMobile ? 0.5 : 0.2,
                                                px: isMobile ? 1.5 : 2,
                                                width: '100%',
                                                border: '1px dashed #7047ef'
                                            }}
                                        >
                                            {addItemButtonText}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Totals Summary */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mb: 0,
                py: 1,
                px: 2,
                flexDirection: totalsLayout,
                alignItems: totalsLayout === 'column' ? 'flex-end' : 'center',
                gap: totalsLayout === 'column' ? 1 : 3,
                flexWrap: 'wrap',
                backgroundColor: "#c7c7c7"
            }}>
                {showTaxableAmount && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: totalsLayout === 'column' ? 1 : 1,
                        minWidth: totalsLayout === 'column' ? '100%' : 'auto'
                    }}>
                        <Typography variant="body2" color="text.secondary" fontSize={getFontSize()}>
                            {taxableAmountLabel}:
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight="medium"
                            color="text.secondary"
                            sx={{ fontWeight: 700 }}
                            fontSize={getFontSize()}
                        >
                            {formatCurrency(taxableAmount)}
                        </Typography>
                    </Box>
                )}

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: totalsLayout === 'column' ? 1 : 1,
                    minWidth: totalsLayout === 'column' ? '100%' : 'auto'
                }}>
                    <Typography variant="body2" color="text.secondary" fontSize={getFontSize()}>
                        {totalDiscountLabel}:
                    </Typography>
                    <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={"text.secondary"}
                        sx={{ fontWeight: 700 }}
                        fontSize={getFontSize()}
                    >
                        {formatCurrency(totalDiscount)}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: totalsLayout === 'column' ? 1 : 1,
                    minWidth: totalsLayout === 'column' ? '100%' : 'auto'
                }}>
                    <Typography variant="body2" color="text.secondary" fontSize={getFontSize()}>
                        {totalTaxLabel}:
                    </Typography>
                    <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={"text.secondary"}
                        sx={{ fontWeight: 700 }}
                        fontSize={getFontSize()}
                    >
                        {formatCurrency(totalTax)}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: totalsLayout === 'column' ? 1 : 1,
                    minWidth: totalsLayout === 'column' ? '100%' : 'auto'
                }}>
                    <Typography variant="body2" fontSize={getFontSize()}>
                        Total:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" fontSize={getFontSize()}>
                        {formatCurrency(totalAmount)}
                    </Typography>
                </Box>
            </Box>

            {/* Product Selection Modal */}
            <Dialog
                open={openProductModal}
                onClose={() => {
                    setOpenProductModal(false);
                    setSelectedProducts([]);
                    setIsSelectAll(false);
                }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ p: isMobile ? 2 : 3 }}>
                    <Box sx={{ display: "flex", justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ShoppingCartIcon />
                            <Typography variant={isMobile ? "h6" : "h6"}>Select Products</Typography>
                        </Box>
                        <Button size='small' variant='outlined' onClick={()=>navigate('/inventory/addProduct')}>
                            Add New Product
                        </Button>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ p: isMobile ? 1 : 2 }}>
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search products by name, part number, or barcode..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                            size={isMobile ? "small" : "medium"}
                        />
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                        <Table size={isMobile ? "small" : "medium"}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" sx={{ fontSize: getFontSize() }}>
                                        <Checkbox
                                            checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                                            indeterminate={selectedProducts.length > 0 && selectedProducts.length < filteredProducts.length}
                                            onChange={handleSelectAll}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: getFontSize() }}>Product Name</TableCell>
                                    <TableCell sx={{ fontSize: getFontSize() }}>Part No</TableCell>
                                    <TableCell sx={{ fontSize: getFontSize() }}>
                                        {isPurchase ? 'Default Price (Edit Later)' : 'Unit Price'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: getFontSize() }}>Stock</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredProducts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            <Typography color="text.secondary">
                                                No products found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredProducts.map((product) => (
                                        <TableRow key={product._id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedProducts.includes(product._id)}
                                                    onChange={() => handleProductSelect(product._id)}
                                                    size={isMobile ? "small" : "medium"}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: getFontSize() }}>{product.name}</TableCell>
                                            <TableCell sx={{ fontSize: getFontSize() }}>{product.partNo || '-'}</TableCell>
                                            <TableCell sx={{ fontSize: getFontSize() }}>
                                                {isPurchase ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Enter manually
                                                    </Typography>
                                                ) : (
                                                    formatCurrency(product.unitPrice || 0)
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: getFontSize() }}>
                                                {product.quantity || 0}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                <DialogActions sx={{ p: isMobile ? 1 : 2 }}>
                    <Button
                        onClick={() => {
                            setOpenProductModal(false);
                            setSelectedProducts([]);
                            setIsSelectAll(false);
                        }}
                        size={isMobile ? "small" : "medium"}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={addSelectedProducts}
                        disabled={selectedProducts.length === 0}
                        startIcon={<AddIcon />}
                        size={isMobile ? "small" : "medium"}
                    >
                        Add Selected ({selectedProducts.length})
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ItemsTableSection;